'use client';
import { useDebounceFn } from 'ahooks';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef } from 'react';
import type { Grid as GridType } from 'react-virtualized';
import { AutoSizer, Grid } from 'react-virtualized';
import { AgentLineScript } from '@/app/components/icons/AgentLineScript';
import { BaseLineMore } from '@/app/components/icons/BaseLineMore';
import { BaseLinePlus } from '@/app/components/icons/BaseLinePlus';
import { Loading } from '@/app/components/icons/Loading';
import { ElementCard } from '@/app/components/shared/ElementCard';
import { SearchEmpty } from '@/app/components/shared/SearchEmpty';
import { Button } from '@/app/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import { Loader } from '@/app/components/ui/loader';
import { SEARCH_EMPTY_ICON } from '@/app/constants';
import { useCheckEnableCreateElement, useDeleteElements, useExpose, useExtraTag, useGlobalModal } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { ElementType } from '@/app/types';
import { cn, reportEvent } from '@/app/utils';
import { usePathname } from '@/i18n/routing';
import type { ElementListProps } from '../../types';

const GRID_PARAMS = {
  columnCount: 5,
  aspectRatio: 1.5,
};

export const ElementList = ({
  className,
  tagSelectors,
  disabledIds,
  tags,
  elements,
  isLoading = false,
  hasNextPage = false,
  enableCreate = true,
  onLoadMore,
  toggleElementSelection,
  deeps,
  onExpose,
  onDelete,
}: ElementListProps) => {
  const t = useTranslations();
  const { allTag } = useExtraTag();
  const { open: openLoginDialog } = useGlobalModal('loginDialog');
  const pathname = usePathname();
  const { openReferenceModalWithData, userInfo } = useStore((state) => ({
    openReferenceModalWithData: state.openReferenceModalWithData,
    userInfo: state.userInfo,
  }));

  const { handleDelete } = useDeleteElements({
    onSuccess: onDelete,
  });

  const goToDelete = (element: ElementType) => {
    reportEvent('element_operation', {
      operation_type: 'delete',
    });
    handleDelete(element);
  };

  const { checkCreateElementEnable } = useCheckEnableCreateElement({
    onSuccess: () => {
      const tagId = tagSelectors?.filter((it) => it !== allTag.id) || [];
      openReferenceModalWithData?.({
        referenceModalOpen: true,
        element: {
          modality: 'image',
          labels: tagId.length > 1 ? [] : tags?.filter((it) => it.id === tagId[0]),
        },
        referenceModalMode: 'create',
        referenceModalType: 'reference',
        referenceModalFrom: 'select',
      });
      reportEvent('create_element_page_view', {
        refer_element: 'element_selector',
      });
    },
  });

  const loadMore = useDebounceFn(
    () => {
      onLoadMore?.();
    },
    { wait: 200 },
  );

  const gridRef = useRef<GridType>(null);
  const exposureRefs = useRef<HTMLDivElement[]>([]);

  const { initObservedIndices } = useExpose(
    (idx) => {
      onExpose?.(idx);
    },
    exposureRefs.current,
    elements,
    [0.1, 1],
  );

  useEffect(() => {
    initObservedIndices();
  }, [deeps]);

  /**
   * 处理容器尺寸变化，强制 Grid 重新计算单元格位置
   */
  const handleResize = useCallback(() => {
    if (gridRef.current) {
      gridRef.current.recomputeGridSize();
    }
  }, []);

  return (
    <div className={cn(className)}>
      {elements.length === 0 && isLoading ? (
        <div className={cn('flex size-full flex-1 items-center justify-center')}>
          <Loader />
        </div>
      ) : (
        <AutoSizer onResize={handleResize}>
          {({ width, height }) => {
            const { columnCount, aspectRatio } = GRID_PARAMS;
            const columnWidth = Math.floor(width / columnCount);
            const rowCount = Math.ceil(((elements?.length ?? 0) + (enableCreate ? 1 : 0)) / columnCount);
            const gridWidth = width;
            const gridHeight = height;
            const rowHeight = columnWidth * aspectRatio;
            return (
              <Grid
                ref={gridRef}
                className="hide-scrollbar overflow-x-hidden"
                width={gridWidth}
                height={gridHeight}
                rowCount={rowCount}
                columnCount={columnCount}
                rowHeight={rowHeight} // 行高增加间距
                columnWidth={columnWidth} // 列宽增加间距
                noContentRenderer={() => (
                  <SearchEmpty
                    emptySrc={SEARCH_EMPTY_ICON.selector}
                    textClassName="text-xs"
                    actionText={!userInfo ? t('header.navLogin') : undefined}
                    onAction={
                      !userInfo
                        ? () => {
                            openLoginDialog();
                            reportEvent('login_pop_exposure', {
                              refer_element: 'elements-selector-empty',
                              refer_page: pathname,
                            });
                          }
                        : undefined
                    }
                  />
                )}
                cellRenderer={({ columnIndex, rowIndex, style }) => {
                  const index = rowIndex * columnCount + columnIndex;
                  // 调整style以添加间距
                  const adjustedStyle = {
                    ...style,
                    left: Number(style.left),
                    top: Number(style.top),
                    width: columnWidth - 4,
                    height: rowHeight - 4,
                  };

                  if (index === 0 && enableCreate) {
                    return (
                      <div
                        style={adjustedStyle}
                        key="create-new"
                        onClick={() => {
                          checkCreateElementEnable({
                            query: {},
                          });
                        }}
                      >
                        <div className="flex h-full w-full cursor-pointer items-center justify-center rounded-8 bg-system-black24">
                          <div className="flex flex-col items-center justify-center">
                            <BaseLinePlus className="size-6" />
                          </div>
                        </div>
                      </div>
                    );
                  }
                  const element = elements[index - (enableCreate ? 1 : 0)];
                  if (!element) return null;

                  const checked = disabledIds?.includes(element.id || '');
                  const isLast = index === elements.length - (enableCreate ? 0 : 1);

                  return (
                    <div
                      key={element.id}
                      style={adjustedStyle}
                      ref={(el: HTMLDivElement) => {
                        exposureRefs.current[index] = el;
                      }}
                      data-index={index}
                    >
                      <ElementCard
                        type="element"
                        element={element}
                        className={cn(
                          'h-full max-h-full w-full max-w-full cursor-pointer',
                          checked && 'border-2 border-system-blue02',
                        )}
                        previewClassName="max-w-full aspect-auto overflow-hidden"
                        imgsContentClassName="aspect-auto h-full"
                        tag={element.tag}
                        onClick={() => {
                          toggleElementSelection?.(element);
                        }}
                        textElementConfig={{
                          textClassName: 'line-clamp-3 leading-normal [font-size:16px]',
                          className: 'p-6',
                        }}
                        slotAfterName={
                          <div className="flex items-center gap-2">
                            <div
                              className="group cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                openReferenceModalWithData?.({
                                  referenceModalOpen: true,
                                  element,
                                  referenceModalMode: 'view',
                                  referenceModalFrom: 'select',
                                  referenceModalFromPage: 'reference_tool',
                                });
                                reportEvent('element_operation', {
                                  operation_type: 'view_detail',
                                });
                              }}
                            >
                              <AgentLineScript className="size-4 text-system-white80 transition-colors group-hover:text-system-white" />
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="text" size="original" className="group size-4 p-0">
                                  <BaseLineMore className="size-4 text-system-white80 transition-colors group-hover:text-system-white" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {element.type === 'user' &&
                                  !['approved', 'pending'].includes(element?.share_meta?.status || '') && (
                                    <DropdownMenuItem
                                      className="min-w-[126px] hover:bg-[#515765]"
                                      onClick={() => {
                                        openReferenceModalWithData?.({
                                          referenceModalOpen: true,
                                          element,
                                          referenceModalMode: 'edit',
                                          referenceModalFrom: 'select',
                                        });
                                        reportEvent('element_operation', {
                                          operation_type: 'edit',
                                        });
                                      }}
                                    >
                                      {t('elements.edit')}
                                    </DropdownMenuItem>
                                  )}
                                <DropdownMenuItem
                                  className="min-w-[126px] hover:bg-[#515765]"
                                  onClick={() => {
                                    goToDelete(element);
                                  }}
                                >
                                  {t('elements.delete')}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        }
                      />
                      {isLast && hasNextPage && isLoading && <Loading className="mx-auto mt-4 animate-spin" />}
                    </div>
                  );
                }}
                onScroll={({ scrollTop, scrollHeight, clientHeight }) => {
                  if (scrollHeight - scrollTop - clientHeight < 100 && !isLoading && hasNextPage) {
                    loadMore.run();
                  }
                }}
              />
            );
          }}
        </AutoSizer>
      )}
    </div>
  );
};
